import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../login/login.css";
import "./TeamRegistration.css";
import { selectAdminToken } from "../../admin/sessionSlice";
import { saveTeamRegistration } from "../../functions/saveTeamRegistration";
import { s3PublicUrl } from "../../utils/s3PublicUrl";

function readGameSettings() {
  try {
    return JSON.parse(sessionStorage.getItem("gameSettings") || "{}") || {};
  } catch {
    return {};
  }
}

function readUserData() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

/** Step 1 of 3 after joining a lobby — collects the team's identity and writes it onto the
 * player's `stages` row (see backend/controllers/saveTeamRegistration.js) before the hunt starts. */
function TeamRegistration() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const { data } = useSelector((state) => state.theme);

  const headingColor = data?.headingColor || "#D83307";
  const buttonBg = data?.buttonBgColor || "#D83307";
  const buttonText = data?.buttonTextColor || "#FFFFFF";
  const rawCover = data?.background_mob || data?.hunt_cover_image || data?.background_desk;
  const coverImage = rawCover ? s3PublicUrl(rawCover) : "";

  useEffect(() => {
    document.body.classList.add("login-static-bg");
    if (coverImage) {
      document.body.style.backgroundImage = `url("${encodeURI(coverImage)}")`;
    }
    return () => {
      document.body.classList.remove("login-static-bg");
      document.body.style.backgroundImage = "";
    };
  }, [coverImage]);

  const storedUserData = useMemo(readUserData, []);
  const gameSettings = useMemo(readGameSettings, []);

  const stageId = location.state?.stageId ?? gameSettings.stageId ?? null;
  const themeName = location.state?.themeName ?? gameSettings.themeName ?? "theme0";

  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? storedUserData.sessionId ?? "";
    const oid = user?.organizationId ?? storedUserData.organizationId ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user, storedUserData]);

  const uid = user?.userId ?? user?.userid ?? user?.id ?? storedUserData.userId ?? storedUserData.userid ?? storedUserData.id;
  const maxTeams = data?.numTeams;

  const [teamNumber, setTeamNumber] = useState("");
  const [teamName, setTeamName] = useState("");
  const [captainName, setCaptainName] = useState("");
  const [warCry, setWarCry] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamNumber.trim() || !teamName.trim() || !captainName.trim() || !warCry.trim()) {
      await Swal.fire({ icon: "warning", title: "Fill in every field", confirmButtonColor: buttonBg });
      return;
    }
    if (!tokenForApi || !uid) return;

    setSaving(true);
    try {
      const resp = await saveTeamRegistration({
        token: tokenForApi,
        stageId,
        userId: uid,
        themeName,
        maxTeams,
        team_number: teamNumber.trim(),
        team_name: teamName.trim(),
        captain_name: captainName.trim(),
        war_cry: warCry.trim(),
      });

      if (resp?.full) {
        await Swal.fire({
          icon: "info",
          title: "Oops!",
          text: "The teams are full for this hunt.",
          confirmButtonColor: buttonBg,
        });
        return;
      }

      sessionStorage.setItem(
        "gameSettings",
        JSON.stringify({
          ...gameSettings,
          stageId,
          themeName,
          team_number: teamNumber.trim(),
          team_name: teamName.trim(),
          captain_name: captainName.trim(),
          war_cry: warCry.trim(),
        })
      );

      navigate("/war-cry", { replace: true, state: { stageId, themeName } });
    } catch (err) {
      await Swal.fire("Error", err?.message || "Failed to save your team", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="team-reg-page">
      {!coverImage && (
        <>
          <div className="team-reg-blob team-reg-blob--mint" />
          <div className="team-reg-blob team-reg-blob--pink" />
          <div className="team-reg-blob team-reg-blob--blue" />
        </>
      )}

      <div className="team-reg-content">
        <div className="team-reg-step" style={{ color: buttonBg }}>STEP 1 OF 3</div>
        <h1 className="team-reg-title" style={{ color: headingColor }}>Register your team</h1>

        <form className="team-reg-form" onSubmit={handleSubmit}>
          <label className="team-reg-field">
            <span className="team-reg-label">Team number</span>
            <input
              className="team-reg-input"
              value={teamNumber}
              onChange={(e) => setTeamNumber(e.target.value)}
              placeholder="e.g. 04"
              inputMode="numeric"
              maxLength={80}
            />
          </label>

          <label className="team-reg-field">
            <span className="team-reg-label">Team name</span>
            <input
              className="team-reg-input"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="e.g. Rangers"
              maxLength={100}
            />
          </label>

          <label className="team-reg-field">
            <span className="team-reg-label">Captain name</span>
            <input
              className="team-reg-input"
              value={captainName}
              onChange={(e) => setCaptainName(e.target.value)}
              placeholder="e.g. Priya S."
              maxLength={100}
            />
          </label>

          <label className="team-reg-field">
            <span className="team-reg-label">Your war cry</span>
            <input
              className="team-reg-input"
              value={warCry}
              onChange={(e) => setWarCry(e.target.value)}
              placeholder="e.g. Louder than the lift"
              maxLength={100}
            />
          </label>

          <button
            type="submit"
            className="team-reg-submit"
            style={{ background: buttonBg, color: buttonText }}
            disabled={saving}
          >
            {saving ? "SAVING..." : "Record war cry"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default TeamRegistration;
